import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from 'react';

export type CustomerAddress = {
  id: string;
  customerId: string;
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  zip: string;
  city: string;
  province: string;
  country: string;
  phone?: string;
  name: string;
  provinceCode: string;
  countryCode: string;
  default: boolean;
};

type UpdateCustomerAddress = (address: CustomerAddress[]) => void;

const CustomerAdressContext = createContext<
  | {
      customerAddress: CustomerAddress[];
      updateCustomerAddress: UpdateCustomerAddress;
    }
  | undefined
>(undefined);

const customerAddressReducer = (state, action) => {
  switch (action.type) {
    case 'update': {
      return {
        customerAddress: action.address,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const CustomerAddressProvider = ({ children }) => {
  const [state, dispatch] = useReducer(customerAddressReducer, {
    customerAddress: undefined,
  });
  const updateCustomerAddress = useCallback(
    (address) => dispatch({ type: 'update', address }),
    [dispatch],
  );
  const customerAddress: CustomerAddress[] = useMemo(
    () => state.customerAddress,
    [state.customerAddress],
  );
  const value = { customerAddress, updateCustomerAddress };
  return (
    <CustomerAdressContext.Provider value={value}>
      {children}
    </CustomerAdressContext.Provider>
  );
};

const useCustomerAddress = () => {
  const context = useContext(CustomerAdressContext);
  if (context == undefined) {
    throw new Error(
      'useCustomerAddress must be used within a CustomerAddressProvider',
    );
  }
  return context;
};

export { CustomerAddressProvider, useCustomerAddress };
